// Centralized content for the interview companion. All copy and case data live here
// so cards, summaries, and detail pages stay consistent.

export const CONTACT = {
  name: "Matt Kolar",
  linkedin: "https://www.linkedin.com/in/mattkolar",
};

export const hero = {
  eyebrow: "Prepared for Kenneth Chestnut",
  headline: "From partner commitments to customer value.",
  supporting:
    "My priorities for Emergent, a partnership I am proud of, and additional work to explore.",
  attribution: "Matt Kolar · LinkedIn and Procore",
};

export const q1 = {
  heading: "What I would prioritize first",
  lead:
    "Turn Emergent's existing partner commitments into useful customer adoption. I would identify the strongest ready initiative, get its dependencies clear, ship it, and use the evidence to improve what comes next.",
  stages: [
    {
      id: "understand",
      label: "Understand and unblock",
      body:
        "Confirm the customer outcome, partner commitments, owners, deadlines, readiness, and baseline. Resolve immediate blockers while learning.",
    },
    {
      id: "ship",
      label: "Ship and activate",
      body:
        "Choose using customer value, partner motivation, expected impact, and delivery readiness. Own the work through launch and adoption.",
    },
    {
      id: "learn",
      label: "Learn and expand",
      body:
        "Evaluate activation, paid conversion, retention, and economics. Improve the approach and expand proven work.",
    },
  ],
  reasoning:
    "The relationships and joint plans already exist. My immediate focus would be getting those commitments into customers' hands.",
  conditionsIntro: "Conditions I would help establish.",
  conditions: [
    { label: "Shared outcome", body: "A clear customer problem and agreed business result." },
    { label: "Partner incentive", body: "A reason for both companies to prioritize execution." },
    { label: "Delivery commitment", body: "Named owners and realistic capacity on both sides." },
    { label: "Useful evidence", body: "A baseline and a way to follow customer outcomes." },
    { label: "Fast decisions", body: "Clear authority to resolve scope, timing, and investment tradeoffs." },
  ],
  expansions: [
    {
      title: "How I would choose the first initiative",
      paragraphs: [
        "I would assess the customer problem, expected business impact, supporting evidence, partner commitment, delivery readiness, and existing obligations. I would focus effort on one or two promising initiatives while keeping every commitment visible. Where scope or timing needs to change, I would raise it early with a recommendation.",
      ],
    },
    {
      title: "An initial hypothesis: adoption after the Claude launch",
      badge: "Hypothesis to validate",
      paragraphs: [
        "Emergent's September 18, 2026 announcement describes building and previewing apps inside Claude, with deployment on Emergent. I would examine that customer journey, choose a proven business use case, and work with the team and Anthropic on a focused activation effort. The actual first initiative would depend on customer data, commitments, and delivery readiness.",
      ],
      source: {
        href: "https://emergent.sh/news/emergent-now-available-as-connector-inside-claude-for-small-business",
        label: "Emergent announcement · September 18, 2026",
      },
    },
    {
      title: "What I would measure",
      rows: [
        { label: "Delivery", body: "Milestones, launch readiness, distribution commitments, and blockers." },
        { label: "Customer value", body: "Qualified arrivals, successful activation, and useful application activity." },
        { label: "Business outcome", body: "Paid conversion, retention, or expansion, as appropriate." },
        { label: "Economics", body: "Partner investment, incentives, and incremental serving costs." },
      ],
      conclusion:
        "Definitions and targets would be agreed with the team. Each initiative needs measures suited to its customer outcome.",
    },
    {
      title: "First 90 days",
      badge: "Proposed sequence; adapt to existing commitments",
      rows: [
        { label: "First 1–2 weeks", body: "Understand customer workflows, own commitments, remove immediate blockers." },
        { label: "By month 1", body: "Ship or materially advance a ready initiative with distribution and measurement." },
        { label: "Month 2", body: "Use feedback and cohort results to improve execution." },
        { label: "Month 3", body: "Expand what works and make the next launch easier to repeat." },
      ],
    },
  ],
};

export const q2 = {
  heading: "A partnership I am proud of",
  subheading: "LinkedIn Recruiter System Connect",
  decisionHeadline: "Changed the partner sequence to get a reluctant ecosystem moving.",
  summary:
    "I led the partnership strategy for Recruiter System Connect. Recruiters were copying information between disconnected systems, while ATS providers were reluctant to share data with a company they viewed as a competitor. I shaped a reciprocal model and shifted toward motivated mid-market partners when the largest providers would not engage. I helped launch the integrations, gathered customer feedback, and used that evidence and advocacy to build broader momentum.",
  role:
    "My contribution: partner strategy, customer advocacy, incentive alignment, and coordination through pilot and launch.",
  outcome:
    "We reduced duplicated work and improved recruiting context. I am proud of finding a practical path through competitive concerns and getting a better workflow into customers' hands.",
};

export const closing = {
  heading: "Questions I would explore with you",
  questions: [
    "Which committed initiative would you want me to own first?",
    "Where do you already see customer impact, and where are we still testing an assumption?",
    "What would make the first 90 days successful?",
  ],
  note: "Built with Emergent to make our conversation useful beyond the interview.",
};

export const workIntro =
  "Five additional examples of how I approach distribution, partner incentives, product decisions, and execution.";

export const filters = [
  "All work",
  "Execution and adoption",
  "Partner incentives",
  "Strategic judgment",
];

// Case data — keyed by slug. `order` drives the /work grid (RSC is featured, not in the five).
export const cases = {
  rsc: {
    slug: "rsc",
    name: "Recruiter System Connect",
    title: "Recruiter System Connect",
    tags: ["Partner incentives", "Execution and adoption"],
    headline: "Changed the partner sequence to get a reluctant ecosystem moving.",
    role: "I led the partnership strategy and coordinated partner work through pilots and launches.",
    outcome:
      "A scalable integration model that reduced duplicated work, improved recruiting-team coordination, and gave recruiters better candidate context.",
    cardOutcome:
      "A scalable integration model that reduced duplicated work and gave recruiters better candidate context.",
    visual: "rsc",
    relevance: null,
    sections: [
      {
        title: "The problem",
        body: [
          "Enterprise recruiters were manually copying candidate information between LinkedIn Recruiter and their applicant tracking systems. They duplicated outreach and lacked visibility into candidate status. The fragmented workflow also created repetitive or conflicting communication for candidates.",
        ],
      },
      {
        title: "The decision",
        body: [
          "The ATS providers we approached viewed LinkedIn as a competitor. I used customer feedback to shape a reciprocal model: relevant recruiting information from the ATS would be available in Recruiter, and a valuable Recruiter experience would be available inside the ATS. When the largest providers remained reluctant, I focused on more agile mid-market partners that wanted to differentiate and move upmarket.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Identified affected customers and coordinated customer advocates.",
          "Developed the partner proposition and changed the partner sequence.",
          "Worked with product and engineering on requirements.",
          "Aligned sales, marketing, legal, and external partners.",
          "Supported pilots, launches, market promotion, and feedback.",
          "Used early traction and customer advocacy to build momentum with larger providers.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "Customer demand, partner motivation, and an achievable launch can create a practical path through a stalled partnership.",
        ],
      },
    ],
    evidence: {
      heading: "Published RSC program outcomes",
      items: [
        { figure: "3.5 hours", label: "Average weekly time savings reported for RSC users." },
        { figure: "7%", label: "Reported increase in InMail engagement." },
      ],
      attribution:
        "LinkedIn published these figures in November 2021 for the broader RSC program. My contribution was partnership strategy and execution; the figures describe the overall program.",
      source: {
        href: "https://www.linkedin.com/business/talent/blog/product-tips/find-quality-candidates-amidst-great-reshuffle",
        label: "LinkedIn Talent Blog · November 2021",
      },
    },
    related: ["learning-lms", "procore-ecosystem"],
  },
  "procore-ecosystem": {
    slug: "procore-ecosystem",
    name: "Procore ecosystem",
    title: "Procore ecosystem",
    tags: ["Strategic judgment", "Execution and adoption"],
    headline: "Helped a three-person team focus on the highest-value partnerships.",
    role: "I was one of three people helping rebuild a more strategic business development and ecosystem function.",
    outcome:
      "A tiered partner program and a clearer basis for directing limited resources toward customer and product value.",
    cardOutcome:
      "A tiered partner program and a clearer basis for directing limited resources toward customer and product value.",
    visual: "procore",
    relevance: "Relevant to owning several partner initiatives with limited resources.",
    sections: [
      {
        title: "The problem",
        body: [
          "Procore had a broad set of partner opportunities, multiple product leaders with competing priorities, and limited partnership capacity. The function was being rebuilt during organizational change.",
        ],
      },
      {
        title: "The decision",
        body: [
          "I helped create a partner program and tiering structure that connected prioritization to customer needs, product priorities, and the team's ability to execute.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Contributed to partner-program design and portfolio prioritization.",
          "Aligned partner opportunities with product leaders.",
          "Coordinated across competing internal stakeholders.",
          "Helped develop and launch partnerships.",
          "Supported new embedded and white-label product experiences.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "The work created a more deliberate way to evaluate opportunities and focus the team. It reinforced the importance of a practical prioritization approach when the team is small and the opportunity set is broad.",
        ],
      },
    ],
    related: ["rsc", "learning-lms"],
  },
  "learning-lms": {
    slug: "learning-lms",
    name: "LinkedIn Learning LMS integrations",
    title: "LinkedIn Learning LMS integrations",
    tags: ["Execution and adoption", "Partner incentives"],
    headline: "Made integrations part of the enterprise buying and adoption experience.",
    role: "I led partnerships with learning management systems and coordinated execution across partner and internal teams.",
    outcome:
      "Easier enterprise deployment and administration, stronger distribution, and support for customers representing more than half of LinkedIn Learning revenue.",
    cardOutcome:
      "Easier enterprise deployment and administration, and stronger distribution across enterprise learning systems.",
    visual: "lms",
    relevance: "Relevant to turning integration availability into customer adoption and commercial value.",
    sections: [
      {
        title: "The problem",
        body: [
          "Enterprises wanted LinkedIn Learning to work within the systems they already used to assign training, track completion, manage compliance programs, and report on learning. A disconnected experience created administrative friction and affected buying decisions.",
        ],
      },
      {
        title: "The decision",
        body: [
          "I treated LMS integrations as a distribution and customer-adoption priority. I focused on the partners most relevant to enterprise needs and helped align product, technical, and go-to-market requirements.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Gathered context on enterprise learning workflows and buying criteria.",
          "Led LMS partner strategy and coordination.",
          "Aligned product, engineering, sales, customer success, and legal stakeholders.",
          "Supported integration requirements for learning discovery and administrative tracking.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "The integrations made LinkedIn Learning easier to evaluate, deploy, manage, and track. Customers using these integrations represented more than 50% of LinkedIn Learning revenue. That figure describes the revenue associated with those customers; it is not revenue sourced or caused solely by my partnerships.",
        ],
      },
    ],
    related: ["learning-lxp", "rsc"],
  },
  "learning-lxp": {
    slug: "learning-lxp",
    name: "LinkedIn Learning LXP",
    title: "LinkedIn Learning LXP",
    tags: ["Partner incentives"],
    headline: "Helped create a model competitors could participate in.",
    role: "I helped shape the partnership and integration model for a broader learning-discovery experience.",
    outcome:
      "A unified discovery experience that gave customers access to learning from multiple providers while preserving partners' core relationships.",
    cardOutcome:
      "A unified discovery experience spanning multiple providers while preserving partners' core relationships.",
    visual: "lxp",
    relevance: "Relevant to aligning partners whose commercial interests overlap.",
    sections: [
      {
        title: "The problem",
        body: [
          "Employees had to search across fragmented learning providers. Customers wanted relevant content to be easier to discover in one place.",
        ],
      },
      {
        title: "The decision",
        body: [
          "I helped position LinkedIn Learning as an additional discovery and distribution channel for providers, including competitors such as Coursera and Udemy. The model preserved separate enterprise agreements, required customers to enable integrations, and established boundaries around how content was surfaced.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Worked with content partners, product, engineering, legal, and enterprise stakeholders.",
          "Helped address concerns about displacement and customer relationships.",
          "Helped define the integration model and participation boundaries.",
          "Explored customer-demand and search insights as a way to create value for partners.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "The work broadened learning discovery and created a viable model for competitor participation. Clear mutual value and boundaries helped address competitive concerns.",
        ],
      },
    ],
    related: ["learning-lms", "application-integrations"],
  },
  "application-integrations": {
    slug: "application-integrations",
    name: "Apply with LinkedIn / Apply Connect",
    title: "Apply with LinkedIn / Apply Connect",
    tags: ["Execution and adoption", "Strategic judgment"],
    headline: "Helped make application partnerships more consistent and scalable.",
    role: "I advised product and partnership teams on partner, data, and integration requirements.",
    outcome:
      "A more structured integration approach that supported candidate experience and clearer privacy, security, and partner requirements.",
    cardOutcome:
      "A more structured integration approach supporting candidate experience and clearer partner requirements.",
    visual: "application",
    relevance: "Relevant to carrying technical partner work across product, engineering, and commercial requirements.",
    sections: [
      {
        title: "The problem",
        body: [
          "Candidates faced fragmented application experiences. The early API model was widely distributed, while the product needed a more consistent approach to data flows, partner implementation, privacy, security, and GDPR readiness.",
        ],
      },
      {
        title: "The decision",
        body: [
          "I helped move the work toward a structured integration framework and contributed to a more seamless in-product application experience through Apply Connect.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Brought recruiting-ecosystem knowledge into product and partner decisions.",
          "Helped define product, partner, data, and integration requirements.",
          "Worked across product, engineering, legal, and commercial stakeholders.",
          "Connected candidate experience with technical and operational constraints.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "The work helped improve consistency across partner integrations and supported a better candidate experience. It showed the value of bringing product, technical, legal, and partner considerations into one workable framework.",
        ],
      },
    ],
    related: ["learning-lxp", "jobs-distribution"],
  },
  "jobs-distribution": {
    slug: "jobs-distribution",
    name: "LinkedIn Jobs distribution",
    title: "LinkedIn Jobs distribution",
    tags: ["Strategic judgment"],
    headline: "Recommended stopping an initiative I had built when the product foundation was not ready.",
    role: "I led an ambiguous, roughly two-year effort, built and managed a team, and evaluated the results.",
    outcome: "A recommendation to redirect investment toward core product and engineering work.",
    cardOutcome: "A recommendation to redirect investment toward core product and engineering work.",
    visual: "jobs",
    relevance: "Relevant to recognizing when a partner initiative needs a different underlying condition to succeed.",
    sections: [
      {
        title: "The problem",
        body: [
          "LinkedIn Jobs needed stronger search visibility and distribution to compete more effectively with Indeed.",
        ],
      },
      {
        title: "What I did",
        bullets: [
          "Built and managed a team.",
          "Developed opportunities with government organizations, libraries, higher education, and search or distribution partners.",
          "Supported related efforts around Google for Jobs.",
          "Evaluated traction and the underlying product and SEO limitations.",
        ],
      },
      {
        title: "The decision",
        body: [
          "As evidence accumulated, I concluded that additional partnerships could not overcome the product and technical constraints. I recommended ending the initiative and redirecting investment toward product and engineering.",
        ],
      },
      {
        title: "Outcome and lesson",
        body: [
          "I was willing to challenge an initiative I had built and make a recommendation based on product readiness. Partnership investment needs a foundation that can benefit from the demand it creates.",
        ],
      },
    ],
    related: ["application-integrations", "procore-ecosystem"],
  },
};

// Order for the /work grid (the five additional examples, RSC is featured separately).
export const workOrder = [
  "procore-ecosystem",
  "learning-lms",
  "learning-lxp",
  "application-integrations",
  "jobs-distribution",
];

export const resume = {
  name: "Matt Kolar",
  title: "Strategic Partnerships | Platform Partnerships | Product Business Development",
  summary:
    "Strategic partnerships and product business development leader with 10+ years developing platform and ecosystem strategy, structuring complex commercial partnerships, and launching product integrations at scale. Led partnerships end-to-end from partner evaluation through negotiation, product development, launch, and GTM. Experienced influencing executives and leading cross-functional teams across Product, Engineering, Legal, Finance, Sales, and Marketing to drive adoption, revenue, and platform growth.",
  experience: [
    {
      role: "Strategic Partnerships Manager — Product & Platform Partnerships",
      company: "Procore Technologies",
      dates: "Sept 2024 – Aug 2025",
      bullets: [
        "Structured and negotiated an embedded product partnership supporting $20M+ in initial contract value leading commercial terms, integration scope, compliance requirements, and cross-functional execution from evaluation through agreement",
        "Developed and launched a tiered ecosystem strategy for a 600+ application marketplace, aligning partner segments and incentives to drive co-sell pipeline and partner attach rates",
        "Led complex integration partnerships with enterprise ERP platforms, aligning partner and internal Product, Engineering, Legal, Marketing, and Sales teams from joint solution design and commercial negotiation through launch and GTM",
        "Built SI enablement and partner-led GTM programs to expand platform distribution into new international markets",
        "Owned strategy and performance for a 300+ partner portfolio, prioritizing investments based on product adoption, engagement, partner value, and broader platform objectives",
      ],
    },
    {
      role: "Senior Business Development Manager — Product & Platform Partnerships",
      company: "LinkedIn",
      dates: "Mar 2016 – Dec 2023",
      bullets: [
        "Promoted 4x during LinkedIn tenure",
        "Developed and executed product partnerships strategy across a 150+ ISV ecosystem supporting LinkedIn Talent and Learning products used by millions of customers globally, identifying priority partners and product integration to drive adoption, revenue, and platform growth",
        "Identified, structured, negotiated, and launched strategic platform partnerships with Microsoft, Google, SAP, Oracle, Workday, and other major technology platforms, leading relationships from business case and partner evaluation through product integration and GTM",
        "Partnered with Product and Engineering to define API integration patterns, authentication models, data sharing requirements, and product scopes for secure integrations across enterprise software ecosystems",
        "Built financial and strategic business cases evaluating partnership economics, adoption potential, development cost, and revenue impact to inform build, buy, or partner decisions",
        "Launched Recruiter System Connect (RSC) integrations across ATS partners, saving recruiters 3.5 hours per week and driving a 7% increase in InMail acceptance rates",
        "Drove LinkedIn Learning integrations across enterprise LMS and HCM platforms, with partner integrations influencing 50%+ of the product's enterprise ARR",
      ],
    },
    {
      role: "Associate, Business Leadership Program — Growth Partnerships",
      company: "LinkedIn",
      dates: "Jul 2015 – Mar 2016",
      bullets: [
        "Secured $1M+ in executive-approved funding for a strategic growth initiative to strengthen LinkedIn Jobs' organic search position, hiring and leading an 8-person distributed team",
      ],
    },
  ],
  education: [
    {
      degree: "MBA",
      school: "Brigham Young University – Marriott School of Business",
      detail: "Top 10% of class | Phi Kappa Phi",
    },
    {
      degree: "B.S. in Communication",
      school: "Brigham Young University–Idaho",
      detail: null,
    },
  ],
  links: [{ label: "linkedin.com/in/mattkolar", href: "https://www.linkedin.com/in/mattkolar" }],
};
